import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  Users, TrendingUp, DollarSign, Target, Award, ChevronRight,
  Flame, Clock, ArrowUp, ArrowDown, LayoutGrid, Activity,
  Coffee, CheckCircle, BarChart3, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { leadsAPI, attendanceAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, sub, delta }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3.5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
          <p className="text-xl font-bold text-slate-900">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
          <Icon className="w-4 h-4 text-teal-600" />
        </div>
      </div>
      {delta != null && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${delta >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
          {delta >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          <span>{Math.abs(delta)}% vs last month</span>
        </div>
      )}
    </div>
  );
}

function ConsultantRow({ consultant, rank }) {
  const navigate = useNavigate();
  const rankColors = ['text-yellow-500', 'text-slate-400', 'text-amber-700'];
  const rankLabels = ['🥇', '🥈', '🥉'];
  return (
    <div
      className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl transition-colors cursor-pointer"
      onClick={() => navigate(`/sales/manager/leads?consultant=${consultant.id}`)}
    >
      <div className="w-8 text-center font-bold text-lg">
        {rank < 3 ? rankLabels[rank] : <span className="text-slate-400 text-sm">#{rank + 1}</span>}
      </div>
      <div className="w-9 h-9 rounded-full bg-teal-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
        {(consultant.name || '?').slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 truncate">{consultant.name}</p>
        <div className="flex items-center gap-3 mt-0.5">
          <div className="flex-1 max-w-[120px]">
            <Progress
              value={consultant.total_leads > 0 ? (consultant.converted / consultant.total_leads) * 100 : 0}
              className="h-1.5"
            />
          </div>
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {consultant.converted}/{consultant.total_leads}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4 text-right flex-shrink-0">
        <div>
          <p className="text-sm font-bold text-teal-600">{consultant.converted}</p>
          <p className="text-xs text-slate-400">converted</p>
        </div>
        <div>
          <p className="text-sm font-bold text-teal-600">{consultant.conversion_rate}%</p>
          <p className="text-xs text-slate-400">rate</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-700">INR{(consultant.pipeline_value / 1000).toFixed(0)}k</p>
          <p className="text-xs text-slate-400">pipeline</p>
        </div>
      </div>
    </div>
  );
}

function AttendanceRow({ log }) {
  const statusConfig = {
    active: { dot: 'bg-emerald-500 animate-pulse', text: 'Active', bg: 'bg-emerald-50' },
    on_break: { dot: 'bg-amber-500', text: 'On Break', bg: 'bg-amber-50' },
    punched_out: { dot: 'bg-slate-400', text: 'Clocked Out', bg: 'bg-slate-50' },
  };
  const cfg = statusConfig[log.status] || statusConfig.punched_out;
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${cfg.bg}`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{log.user_full_name}</p>
        <p className="text-xs text-slate-500">
          {log.punch_in_time ? `In: ${new Date(log.punch_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '—'}
          {log.elapsed_minutes != null ? ` · ${Math.floor(log.elapsed_minutes / 60)}h ${log.elapsed_minutes % 60}m` : ''}
        </p>
      </div>
      <Badge className={`text-xs ${
        log.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
        log.status === 'on_break' ? 'bg-amber-100 text-amber-700' :
        'bg-slate-100 text-slate-600'
      }`}>
        {cfg.text}
      </Badge>
    </div>
  );
}

export default function SalesManagerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [aging, setAging] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, attendanceRes, agingRes] = await Promise.allSettled([
        leadsAPI.getPipelineAnalytics(),
        attendanceAPI.getTeam(),
        leadsAPI.getAgingSummary(),
      ]);
      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
      if (attendanceRes.status === 'fulfilled') setAttendance(attendanceRes.value.data.logs || []);
      if (agingRes.status === 'fulfilled') setAging(agingRes.value.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sc = analytics?.stage_counts || {};
  const totalLeads = analytics?.total_leads || 0;
  const pipelineValue = analytics?.pipeline_value || 0;
  const convRate = analytics?.conversion_rate || 0;
  const topConsultants = analytics?.top_consultants || [];

  const activeNow = attendance.filter((l) => l.status === 'active').length;
  const onBreak = attendance.filter((l) => l.status === 'on_break').length;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare overflow-y-auto bg-slate-50">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Sales Overview</h1>
            <p className="text-sm text-slate-500">Team performance and pipeline analytics</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/sales/reports')} className="gap-1.5">
              <BarChart3 className="w-4 h-4" /> Reports
            </Button>
            <Button onClick={() => navigate('/sales/manager/leads')} className="bg-teal-600 hover:bg-teal-700 gap-1.5">
              <LayoutGrid className="w-4 h-4" /> Pipeline Board
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard icon={Users} label="Total Leads" value={totalLeads} color="bg-blue-500" />
          <StatCard icon={Activity} label="In Discussion" value={sc.in_discussion || 0} color="bg-violet-500" />
          <StatCard icon={Flame} label="Prospective" value={sc.prospective || 0} color="bg-amber-500" />
          <StatCard icon={CheckCircle} label="Registered" value={sc.registered || 0} color="bg-teal-500" />
          <StatCard
            icon={DollarSign}
            label="Pipeline Value"
            value={`INR${(pipelineValue / 1000).toFixed(0)}k`}
            color="bg-emerald-500"
          />
          <StatCard
            icon={TrendingUp}
            label="Conv. Rate"
            value={`${convRate}%`}
            color="bg-indigo-500"
          />
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leaderboard */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Award className="w-4 h-4 text-yellow-500" /> Team Leaderboard
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={fetchData} className="text-xs text-slate-500 gap-1">
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-14 bg-slate-50 rounded-xl animate-pulse" />
                    ))}
                  </div>
                ) : topConsultants.length === 0 ? (
                  <p className="text-center text-sm text-slate-400 py-8">No consultants yet</p>
                ) : (
                  <div className="space-y-1">
                    {topConsultants.map((c, i) => (
                      <ConsultantRow key={c.id} consultant={c} rank={i} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right panel */}
          <div className="space-y-4">
            {/* Live attendance */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-teal-600" /> Team Attendance
                  <div className="ml-auto flex items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                      {activeNow} active
                    </Badge>
                    {onBreak > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs gap-1">
                        <Coffee className="w-3 h-3" /> {onBreak}
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-1.5">
                {attendance.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No team activity today</p>
                ) : (
                  attendance.map((log) => (
                    <AttendanceRow key={log.id || log.user_id} log={log} />
                  ))
                )}
              </CardContent>
            </Card>

            {/* Pipeline funnel with conversion % */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Pipeline Funnel</CardTitle>
                  <button className="text-xs text-teal-600 hover:underline" onClick={() => navigate('/sales/reports')}>
                    Full Report →
                  </button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-2.5">
                {[
                  { key: 'new_lead', label: 'New Leads', color: 'bg-blue-400' },
                  { key: 'in_discussion', label: 'In Discussion', color: 'bg-violet-400' },
                  { key: 'prospective', label: 'Prospective', color: 'bg-amber-400' },
                  { key: 'payment_received', label: 'Payment Received', color: 'bg-emerald-400' },
                  { key: 'registered', label: 'Registered', color: 'bg-teal-400' },
                ].map(({ key, label, color }) => {
                  const count = sc[key] || 0;
                  const pct = totalLeads > 0 ? Math.round((count / totalLeads) * 100) : 0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-600">{label}</span>
                        <div className="flex gap-2">
                          <span className="font-semibold text-slate-700">{count}</span>
                          <span className="text-slate-400">{pct}%</span>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${color} transition-all duration-700 rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* SLA Aging */}
            {aging && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" /> SLA Health
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {[
                    { key: 'healthy', label: 'Healthy', sub: '≤3d', dot: 'bg-emerald-400', text: 'text-emerald-700' },
                    { key: 'at_risk', label: 'At Risk', sub: '4-7d', dot: 'bg-amber-400', text: 'text-amber-700' },
                    { key: 'critical', label: 'Critical', sub: '7d+', dot: 'bg-rose-500', text: 'text-rose-700' },
                  ].map(({ key, label, sub, dot, text }) => (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                      <span className="text-slate-600 flex-1">{label} <span className="text-xs text-slate-400">({sub})</span></span>
                      <span className={`font-bold ${text}`}>{aging[key] || 0}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
